import type { MarketplaceSettings } from '@ralph/contracts'

type QuotaKey = keyof MarketplaceSettings['quotas']

/**
 * `admin.settings` — every word on the studio's Settings tab.
 *
 * Its own namespace since PRD-26 reshaped the form: the review thresholds, the sensitive
 * groups and the creation groups are gone — the standing rule decides what needs review
 * (D-13) and everyone may build (D-22) — and the limits come in two sets, one for a person
 * and a larger one for a group (D-41). The two sets use the same words for the same things
 * ("Knowledge bases", "Documents per base"), under headings that say whose they are, so the
 * only thing a reader has to compare is the numbers.
 *
 * `quotaLabels` is guarded against the contract's own quota keys, so a limit added to
 * `MarketplaceSettingsSchema` is a compile error here rather than an unlabelled field.
 */

export const settings = {
  title: 'Marketplace policy',
  lead: 'Categories, limits, and how long a retired name is held. These are policy values — nothing a maker sends can change them.',
  loading: 'Loading settings…',
  failed: 'Settings could not be loaded.',
  save: 'Save settings',
  saving: 'Saving…',
  saved: 'Saved',
  saveFailed: 'Settings could not be saved.',

  categoriesTitle: 'Categories',
  categoriesHint: 'The taxonomy makers choose from. The order here is the order they see.',

  personalTitle: 'What one person may hold',
  personalHint:
    'What anybody may create on their own. Makers see these as plain limits before they reach one. An administrator’s own knowledge bases have no limit.',
  groupTitle: 'What one group may hold',
  groupHint:
    'Larger, because a group’s managers were appointed by an administrator, and what a group keeps outgrows what one person does. Moving something into a group counts against the group’s limits.',

  quotaLabels: {
    agentsPerMaker: 'Agents',
    basesPerMaker: 'Knowledge bases',
    documentsPerBase: 'Documents per base',
    storagePerBaseBytes: 'Storage per base (MB)',
    agentsPerGroup: 'Agents',
    basesPerGroup: 'Knowledge bases',
    documentsPerGroupBase: 'Documents per base',
    storagePerGroupBaseBytes: 'Storage per base (MB)',
    previewRunsPerMakerPerDay: 'Preview runs per day'
  } satisfies Record<QuotaKey, string>,

  holdTitle: 'Archived name hold (days)',
  holdHint: 'How long a retired listing keeps its name before somebody else can use it.',

  add: 'Add',
  addTo: (list: string): string => `Add to ${list}`,
  remove: (value: string): string => `Remove ${value}`,
  empty: 'empty'
}
